import { isPermalinkPath } from '../../consts';
import { canFollowSeoRedirectToUrl } from '../can-follow-seo-redirect';

export type SeoResolveStatusProbe = (path: string) => Promise<'ok' | 'not_found' | 'error'>;

/**
 * Whether proxy should follow /seo/redirect onto `toUrl` from `fromPath`.
 *
 * Rules (match fandom/character; tags historically looped without the from-path guard):
 * 1. Live permalink source (resolve ok) → never leave it (backend may still emit
 *    permalink → GET for tags, which loops with GET → permalink).
 * 2. Disabled permalink source (resolve 404) → allow GET fallback; allow another
 *    permalink only if that target resolves.
 * 3. Catalog/legacy GET source → follow permalink targets only when they resolve
 *    (canFollowSeoRedirectToUrl).
 *
 * The probe receives a pathname string — never an options object — matching
 * getSeoResolveSSR(path?, pageKey?).
 */
export const shouldFollowSeoRedirectTarget = async (
  toUrl: string,
  probeResolve: SeoResolveStatusProbe,
  fromPath = '',
): Promise<boolean> => {
  const toPath = toUrl.split('?')[0] ?? '';
  const fromPathname = fromPath.split('?')[0] ?? '';

  if (fromPathname && isPermalinkPath(fromPathname)) {
    const fromStatus = await probeResolve(fromPathname);
    if (fromStatus === 'ok') {
      // Already on a live SEO page — stay put even if redirect points at GET.
      return false;
    }

    if (!isPermalinkPath(toPath)) {
      // Disabled SEO permalink → canonical GET listing.
      return true;
    }

    const toStatus = await probeResolve(toPath);
    return toStatus === 'ok';
  }

  if (!isPermalinkPath(toPath)) {
    return canFollowSeoRedirectToUrl(toUrl, false);
  }

  const status = await probeResolve(toPath);
  return canFollowSeoRedirectToUrl(toUrl, status === 'ok');
};
